import { trackError } from '@billy/track-error'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvalidateBills } from '@features/bills/hooks/useInvalidateBills'
import { useInvalidateInvoices } from '@features/invoices/hooks/useInvalidateInvoices'
import { InvoiceType } from '@features/invoicesLegacy/types/invoiceType'
import { ContactBalance } from '@modules-deprecated/app/contacts'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCreatePrepayment } from '@views/bills/hooks/useCreatePrePayment'
import { CreatePrepaymentResponseData } from '@views/bills/query-api'

import { useSegment } from '../../../hooks'
import { formatDate } from '../../../utils'
import { BillType } from '../../bills/enums/billType'
import { Contact } from '../../contacts/types/contact'
import { getReferenceFromSubject, getTypeFromPayableSubject } from '../../paymentReconciliationModal/utils'
import { PayableSubject } from '../types'
import { notifyPaymentRegistrationError } from '../utils/notifyPaymentRegistrationError'

interface UseRegisterPrepaymentProps {
  paymentDate: Date | undefined | null
  subject?: PayableSubject
  contact: Contact | undefined
  contactBalance: ContactBalance | undefined
  onSuccess: (data: CreatePrepaymentResponseData) => void
}

export const useRegisterPrepayment = ({
  paymentDate,
  subject,
  contact,
  contactBalance,
  onSuccess,
}: UseRegisterPrepaymentProps) => {
  const [isProcessing, setIsProcessing] = useState(false)

  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { invalidateBill, invalidateBills } = useInvalidateBills()
  const { invalidateInvoice, invalidateInvoices, invalidateExternalInvoice, invalidateExternalInvoices } =
    useInvalidateInvoices()

  if (!subject) {
    trackError('Subject is required')
  }

  const subjectType = useMemo(() => getTypeFromPayableSubject(subject), [subject])
  const { create: createPrepayment } = useCreatePrepayment({
    onSuccess: (data) => {
      if (subjectType === BillType.Bill) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        track('Bill Payment Created (Client)', { bill_id: subject?.id, is_auto_matched: false, payment_mode: 'manual' })
        invalidateBill(subject?.id)
        invalidateBills()
      } else if (subjectType === InvoiceType.Invoice) {
        track('Invoice Payment Created (Client)', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          invoice_id: subject?.id,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          is_auto_matched: false,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          payment_mode: 'manual',
        })
        invalidateInvoice(subject?.id)
        invalidateExternalInvoice(subject?.id)
        invalidateInvoices()
        invalidateExternalInvoices()
      }

      setIsProcessing(false)
      onSuccess(data)
    },
    onError: (e) => {
      notifyPaymentRegistrationError(t, e?.message)
      setIsProcessing(false)
    },
  })

  const registerContactBalancePayment = useCallback(() => {
    if (!subject?.balance || !contactBalance) {
      return
    }
    track('xxx - expenses - Prepayment modal - register payment clicked')
    setIsProcessing(true)
    const contactBalanceAmount = contactBalance?.amount ?? 0
    const prepaymentAmount = Math.min(subject.balance, contactBalanceAmount)
    const subjectReference = getReferenceFromSubject(subject.id, subjectType)
    const payload = {
      amount: prepaymentAmount,
      associations: [
        {
          subjectReference,
        },
      ],
      contactId: contact?.id as string,
      entryDate: formatDate(paymentDate) as string,
      organizationId: organization?.id as string,
    }
    createPrepayment(payload)
  }, [subject, contact?.id, contactBalance, createPrepayment, organization?.id, paymentDate, track, subjectType])

  return {
    registerContactBalancePayment,
    isProcessing,
  }
}
